/**
 * TransportRulesPage Component
 *
 * Container component for managing Exchange Online transport rules.
 *
 * Features:
 * - Load and display transport rules from backend
 * - Create new rules via modal dialog
 * - Enable/disable/delete existing rules
 * - Update rule priorities
 * - Refresh rules on demand
 * - Error handling and loading states
 * - Tenant context validation
 */

import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Body1,
  Button,
  Spinner,
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableColumnDefinition,
  TableCellLayout,
  createTableColumn,
  Badge,
  Text,
} from '@fluentui/react-components';
import {
  DeleteRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { DataGridCard } from '../../layouts';
import { createColumnWithRowActions } from '../../layouts/factories/columns';
import { useTransportRules } from '../../../hooks/useTransportRules';
import { Alert } from '../../common/Alert';
import type { TransportRule } from '../../../types/transportRules';
import { getRuleType, TRANSPORT_RULE_TYPE_DISPLAY_NAMES } from '../../../types/transportRules';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
  },
});

// Column sizing options (without 'select' - handled by DataGridCard)
const columnSizingOptions = {
  name: {
    minWidth: 150,
    defaultWidth: 300,
  },
  type: {
    minWidth: 120,
    defaultWidth: 180,
  },
  state: {
    minWidth: 80,
    defaultWidth: 120,
  },
  priority: {
    minWidth: 60,
    defaultWidth: 80,
  },
};

interface TransportRulesPageProps {
  tenantId: string;
  onSelectionChange: (selection: Set<string>) => void;
  onRuleCountChange: (count: number) => void;
  onEdit: (rule: TransportRule) => void;
}

export interface TransportRulesPageHandle {
  openBulkDeleteDialog: () => void;
}

export const TransportRulesPage = forwardRef<TransportRulesPageHandle, TransportRulesPageProps>(function TransportRulesPage({
  tenantId,
  onSelectionChange,
  onRuleCountChange,
  onEdit
}, ref) {
  const styles = useStyles();

  // Use React Query hook for data fetching and mutations
  const {
    rules: rawRules,
    isLoading,
    error: fetchError,
    deleteRule,
    deleteError,
  } = useTransportRules(tenantId);

  // Convert undefined to empty array for easier handling
  const rules: TransportRule[] = rawRules ?? [];

  // Selection state for bulk operations
  const [selectedRules, setSelectedRules] = useState<Set<string>>(new Set());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Single delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<TransportRule | null>(null);

  // Operation loading state
  const [operationInProgress, setOperationInProgress] = useState<string | null>(null);

  // Notify parent of rule count changes
  useEffect(() => {
    onRuleCountChange(rules.length);
  }, [rules.length, onRuleCountChange]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange(selectedRules);
  }, [selectedRules, onSelectionChange]);

  // Expose openBulkDeleteDialog method to parent via ref
  useImperativeHandle(ref, () => ({
    openBulkDeleteDialog: () => {
      if (selectedRules.size > 0) {
        setBulkDeleteDialogOpen(true);
      }
    }
  }), [selectedRules.size]);

  // Reset to page 1 when rules list changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rules.length]);

  // Note: Enable/Disable/UpdatePriority handlers removed - these operations
  // are now handled through the edit modal using unified PATCH API

  const openDeleteDialog = (ruleId: string) => {
    const rule = rules.find((r) => r.Identity === ruleId);
    if (rule) {
      setRuleToDelete(rule);
      setDeleteDialogOpen(true);
    }
  };

  const handleEditClick = (ruleId: string) => {
    const rule = rules.find((r) => r.Identity === ruleId);
    if (rule) {
      onEdit(rule);
    }
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;

    setOperationInProgress('Deleting transport rule...');
    try {
      await deleteRule(ruleToDelete.Identity);
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    } catch {
      // Error is captured in hook's deleteError
    } finally {
      setOperationInProgress(null);
    }
  };

  // Handle selection change from DataGridCard
  const handleSelectionChangeFromGrid = (ids: Set<string | number>) => {
    const stringIds = new Set<string>();
    ids.forEach((id) => stringIds.add(String(id)));
    setSelectedRules(stringIds);
  };

  // Column definitions (without 'select' - handled by DataGridCard)
  const columns: TableColumnDefinition<TransportRule>[] = [
    createColumnWithRowActions<TransportRule>({
      columnId: 'name',
      header: 'Name',
      compare: (a, b) => a.Name.localeCompare(b.Name),
      renderContent: (rule) => (
        <span title={rule.Name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rule.Name}
        </span>
      ),
      getActions: (rule) => [
        {
          label: 'Edit',
          icon: <EditRegular />,
          onClick: () => handleEditClick(rule.Identity),
        },
        {
          label: 'Delete',
          icon: <DeleteRegular />,
          onClick: () => openDeleteDialog(rule.Identity),
        },
      ],
      getAriaLabel: (rule) => `Actions for ${rule.Name}`,
    }),
    createTableColumn<TransportRule>({
      columnId: 'type',
      compare: (a, b) => {
        const typeA = getRuleType(a);
        const typeB = getRuleType(b);
        return typeA.localeCompare(typeB);
      },
      renderHeaderCell: () => 'Type',
      renderCell: (rule) => {
        const ruleType = getRuleType(rule);
        const displayName = ruleType !== 'unknown'
          ? TRANSPORT_RULE_TYPE_DISPLAY_NAMES[ruleType]
          : 'Unknown';
        return (
          <TableCellLayout>
            <Text>{displayName}</Text>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<TransportRule>({
      columnId: 'state',
      compare: (a, b) => a.State.localeCompare(b.State),
      renderHeaderCell: () => 'State',
      renderCell: (rule) => (
        <TableCellLayout>
          <Badge
            appearance="filled"
            color={rule.State === 'Enabled' ? 'success' : 'warning'}
          >
            {rule.State}
          </Badge>
        </TableCellLayout>
      ),
    }),
    createTableColumn<TransportRule>({
      columnId: 'priority',
      compare: (a, b) => a.Priority - b.Priority,
      renderHeaderCell: () => 'Priority',
      renderCell: (rule) => (
        <TableCellLayout>
          <Text>{rule.Priority}</Text>
        </TableCellLayout>
      ),
    }),
  ];

  // Bulk delete handler
  const handleBulkDelete = async () => {
    const selectedIds = Array.from(selectedRules);
    setBulkDeleting(true);

    try {
      // Delete each rule individually (matches app pattern from ConditionalAccessPolicies)
      // Hook will auto-invalidate cache after each successful delete
      for (const ruleId of selectedIds) {
        try {
          await deleteRule(ruleId);
        } catch {
          // Continue with remaining deletions, errors captured in hook
        }
      }
      setSelectedRules(new Set());
    } finally {
      setBulkDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(rules.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRules = rules.slice(startIndex, endIndex);

  // Combine fetch and delete errors
  const error = fetchError?.message ?? deleteError?.message ?? null;

  return (
    <div className={styles.container}>
      {error && <Alert intent="error">{error}</Alert>}

      <DataGridCard
        items={paginatedRules}
        columns={columns}
        getRowId={(rule) => rule.Identity}
        columnSizingOptions={columnSizingOptions}
        selectable
        selectedIds={selectedRules}
        onSelectionChange={handleSelectionChangeFromGrid}
        pagination={{
          showing: paginatedRules.length,
          total: rules.length,
          page: currentPage,
          totalPages: totalPages || 1,
          onPageChange: setCurrentPage,
        }}
        loading={isLoading}
        emptyMessage="No transport rules found"
      />

      {/* Single Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Transport Rule</DialogTitle>
            <DialogContent>
              {operationInProgress === 'Deleting transport rule...' ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: tokens.spacingVerticalL,
                  padding: tokens.spacingVerticalXXL
                }}>
                  <Spinner size="large" label="Deleting rule..." />
                  <Body1>This operation may take some time. Please wait...</Body1>
                </div>
              ) : (
                <Body1>
                  Are you sure you want to delete the rule &ldquo;{ruleToDelete?.Name}&rdquo;? This action cannot be undone.
                </Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={operationInProgress !== null}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleDeleteRule}
                disabled={operationInProgress !== null}
              >
                {operationInProgress === 'Deleting transport rule...' ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={(_, data) => setBulkDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
            <DialogContent>
              {bulkDeleting ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: tokens.spacingVerticalL,
                  padding: tokens.spacingVerticalXXL
                }}>
                  <Spinner size="large" label="Deleting rules..." />
                  <Body1>Please wait while rules are being deleted.</Body1>
                </div>
              ) : (
                <>
                  <Body1>
                    Are you sure you want to delete {selectedRules.size} {selectedRules.size === 1 ? 'rule' : 'rules'}? This action cannot be undone.
                  </Body1>
                  <div style={{ marginTop: '16px' }}>
                    <Body1 style={{ fontWeight: 600 }}>Rules to be deleted:</Body1>
                    <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                      {Array.from(selectedRules).map(ruleId => {
                        const rule = rules.find(r => r.Identity === ruleId);
                        if (rule) {
                          return (
                            <li key={ruleId} style={{ marginBottom: '4px' }}>
                              {rule.Name}
                            </li>
                          );
                        }
                        return null;
                      })}
                    </ul>
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setBulkDeleteDialogOpen(false)}
                disabled={bulkDeleting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Operation Progress Modal */}
      <Dialog open={operationInProgress !== null} modalType="modal">
        <DialogSurface>
          <DialogBody>
            <DialogContent>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: tokens.spacingVerticalL,
                padding: tokens.spacingVerticalXXL
              }}>
                <Spinner size="large" label={operationInProgress || ''} />
                <Body1>This operation may take some time. Please wait...</Body1>
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
});
